<template>
  <section>
    <v-container>
      <v-row>
        <v-col :cols="smallChip ? 12 : 6">
          <v-select
            v-model="site_id"
            :rules="rules.site_id"
            :items="sites.data"
            label="Site"
            item-text="hostname"
            item-value="id"
            required
          />
        </v-col>
        <v-col :cols="smallChip ? 12 : 6">
          <v-combobox
            v-model="links"
            :rules="rules.links"
            :disabled="!siteSelected"
            :loading="!siteSelected"
            :items="links"
            label="Links"
            class="pt-1"
            dense
            multiple
            chips
          >
            <v-file-input
              slot="append-outer"
              class="pt-0"
              accept=".csv, application/vnd.ms-excel"
              hide-input
              @change="handleUploadLinks"
            />
          </v-combobox>
        </v-col>
      </v-row>
      <v-row v-if="!canFetchPages">
        <v-col>
          <v-alert type="info">Select a site and add some links or import replacements to continue!</v-alert>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-btn
            id="fileInputButton"
            :disabled="!canBulkImport"
            :loading="loading"
            color="primary"
            class="relative mr-4"
          >
            <v-icon left>cloud_upload</v-icon>
            <span>Bulk Import</span>
            <input id="fileInputField" type="file" accept=".csv, application/vnd.ms-excel" @change="handleBulkImport" />
          </v-btn>
          <v-btn :disabled="!canFetchPages" :loading="loading" color="primary" class="mr-4" @click="handleFetchPages">
            Fetch Pages
          </v-btn>
        </v-col>
      </v-row>
    </v-container>

    <v-container>
      <v-row>
        <v-col>
          <v-card :disabled="!canFetchPages || loading" :loading="!canFetchPages || loading" outlined>
            <v-simple-table :loading="loading">
              <thead>
                <tr>
                  <th class="text-left">Text</th>
                  <th class="text-left">Link</th>
                  <th class="text-left">Wordpress</th>
                  <th class="text-left">Contzilla</th>
                  <th class="text-left">Update</th>
                  <th class="text-left">Delete</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(page, index) in pages" :key="index">
                  <td>
                    <v-text-field v-model="page.text" />
                  </td>
                  <td>
                    <v-text-field
                      v-model="page.link"
                      :hint="page.importData && page.importData.link"
                      :persistent-hint="!!page.importData"
                    />
                  </td>
                  <td>
                    <a :href="page.permalink" target="_blank">
                      <tick-or-cross :value="!!page.wordpress" />
                    </a>
                  </td>
                  <td>
                    <a :href="page.permalink" target="_blank">
                      <tick-or-cross :value="!!page.contzilla" />
                    </a>
                  </td>
                  <td>
                    <v-checkbox v-model="page.update" @change="handleTypeChange('update', page)" />
                  </td>
                  <td>
                    <v-checkbox v-model="page.delete" @change="handleTypeChange('delete', page)" />
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-btn :disabled="!canFetchPages" :loading="loading" color="error" class="mr-4" @click="reset">Reset</v-btn>
          <v-btn
            :disabled="!canReplaceLinks"
            :loading="loading"
            color="primary"
            class="ml-auto"
            @click="handleReplaceLinks"
          >
            Replace Links
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </section>
</template>

<script>
import { chunk } from 'lodash-es'
import { mapActions, mapGetters } from 'vuex'
import { readCsv } from '@/utils/csv'
import PermissionsHandler from '@/mixins/permissions-handler'
import TickOrCross from '@/components/tick-or-cross'
export default {
  name: 'ContentLinkManagement',
  components: { TickOrCross },
  mixins: [PermissionsHandler],
  data() {
    return {
      rules: {
        links: [
          v => !!v || 'Link is required',
          v => (!!v && v.length > 0) || 'You must add some links before you can continue',
        ],
        site_id: [v => !!v || 'Site is required'],
      },
      loading: false,
      site_id: null,
      importData: [],
      links: [],
      pages: [],
    }
  },
  computed: {
    ...mapGetters({
      sites: 'sites/getSites',
      getPages: 'pages/getPages',
    }),
    smallChip() {
      return this.links.length >= 10
    },
    siteSelected() {
      if (this.loading) return true
      return !!this.site_id
    },
    linkSelected() {
      if (this.loading) return true
      return this.links.length > 0
    },
    canBulkImport() {
      if (this.loading) return true
      return this.siteSelected
    },
    canFetchPages() {
      if (this.loading) return true
      return this.siteSelected && this.linkSelected
    },
    canReplaceLinks() {
      if (this.loading) return true
      return this.pages.some(page => page.delete || page.update)
    },
    canUseDataFromImport() {
      return this.importData.length > 0
    },
  },
  async mounted() {
    await this.fetchSites({ paginate: { perPage: null }, filter: { type: 'organic' } })
  },
  methods: {
    ...mapActions({
      fetchSites: 'sites/fetch',
      fetchPages: 'pages/fetchBulk',
      updateContzillaLinks: 'pages/updateLinks',
      fetchWordpressLinks: 'sites/fetchWordpressLinks',
      updateWordpressLinks: 'sites/updateWordpressLinks',
    }),
    reset() {
      this.pages = []
      this.site_id = null
      this.links = []
      this.loading = false
      this.importData = []
      document.getElementById('fileInputField').value = ''
    },
    handleTypeChange(type, page) {
      if (page[type] && type === 'update') page.delete = false
      if (page[type] && type === 'delete') page.update = false
    },
    async handleUploadLinks(file) {
      const fileReader = new FileReader()
      fileReader.addEventListener('load', async event => {
        const data = Array.from(new Set([...event.target.result.split('\n'), ...this.links]))
        if (data.length > 300)
          return this.$notify({ type: 'error', text: 'You can only upload a maximum of 300 links' })
        this.links = data
      })
      fileReader.readAsBinaryString(file)
      this.$nextTick(() => window.scrollTo(0, document.body.scrollHeight))
    },
    async handleBulkImport(event) {
      const [file] = event.target.files
      const data = await readCsv(file)
      if (data.length > 300) return this.$notify({ type: 'error', text: 'You can only upload a maximum of 300 links' })
      this.links = data.map(item => item.link)
      this.importData = data
      this.$nextTick(() => window.scrollTo(0, document.body.scrollHeight))
    },
    async handleFetchPages() {
      this.loading = true
      this.pages = []
      const filters = { site_id: this.site_id, contentLikeAny: this.links }
      const paginate = { perPage: null }
      await Promise.all([
        this.fetchWordpressLinks({ siteId: this.site_id, links: this.links })
          .then(({ data: { data: pages } }) => this.handleRemapData(pages, 'wordpress'))
          .catch(err => this.$notify({ type: 'error', text: 'Unable to retrieve wordpress links ' + err })),

        this.fetchPages({ filters, paginate })
          .then(() => this.handleRemapData(this.getPages.data, 'contzilla'))
          .catch(err => this.$notify({ type: 'error', text: 'Unable to retrieve contzilla links ' + err })),
      ])
      this.loading = false
    },
    handleRemapData(pages = [], type = 'contzilla') {
      const defaultValues = {
        contzilla: type === 'contzilla',
        wordpress: type === 'wordpress',
        update: false,
        delete: false,
      }

      for (const page of pages) {
        const content = typeof page.content === 'string' ? page.content : JSON.stringify(page.content)
        if (!content || (content && content.length === 0)) continue

        const matchedLinks = content.replace(/\\/g, '').match(/<a[^>]+href=\"(.*?)\"[^>]*>(.*?)<\/a>/gm)
        if (!matchedLinks || (matchedLinks && matchedLinks.length === 0)) continue

        const links = matchedLinks.map(html => {
          const formattedHtml = type === 'contzilla' ? html.replace(/"/gm, '\\"') : html
          const regex = /<a[^>]+href=\"(.*?)\"[^>]*>(.*?)<\/a>/gm.exec(html)
          return { html: formattedHtml, link: regex[1], text: regex[2] }
        })

        for (const link of this.links) {
          const foundLinks = links.filter(item => item.link == link)
          for (const foundLink of foundLinks) {
            if (this.canUseDataFromImport) {
              const importData = this.importData.find(item => item.link === foundLink.link)
              if (importData) {
                defaultValues.importData = importData
                foundLink.link = importData.replacement
                defaultValues.delete = importData.delete === '1'
                defaultValues.update = importData.update === '1'
              }
            }
            this.pages.push({ id: page.id, permalink: page.permalink, ...foundLink, ...defaultValues })
          }
        }
      }
    },
    async handleReplaceLinks() {
      this.loading = true
      const replaceLinks = this.pages.filter(page => page.update === true || page.delete === true)
      const contzillaLinks = chunk(
        replaceLinks.filter(page => page.contzilla === true),
        500
      )
      const wordpressLinks = chunk(
        replaceLinks.filter(page => page.wordpress === true),
        500
      )

      for (const links of wordpressLinks) {
        await this.updateWordpressLinks({ siteId: this.site_id, links }).catch(() => {
          this.loading = false
          this.$notify({ type: 'error', text: 'There was an issue updating the wordpress links.' })
        })
      }

      for (const links of contzillaLinks) {
        await this.updateContzillaLinks({ links }).catch(() => {
          this.loading = false
          this.$notify({ type: 'error', text: 'There was an issue updating the wordpress links.' })
        })
      }

      this.reset()
      this.loading = true
      this.$notify({ type: 'success', text: 'All links where replaced' })
    },
  },
}
</script>

<style>
#fileInputButton {
  position: relative;
}

#fileInputField {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
  opacity: 0;
}
</style>
